/**
 * The focused upgrade: ONE plan, one primary button, the grid a tap away.
 *
 *   - Feature gate: the cheapest plan that unlocks the feature (or the next
 *     tier up) — "Upgrade to Starter — $19/mo".
 *   - Trial: start the trialled plan now — "Start Growth now — $49/mo" —
 *     through POST /api/billing/trial/convert (ends the Stripe trial on the
 *     existing subscription; collects a card first if none is on file).
 *   - Paid "Change plan": the next tier up, with Stripe's proration preview.
 *   - No live subscription: nothing to change in place, so the full grid
 *     (BillingUpgradeModal) opens on the chosen plan instead.
 *
 * Open it with useDirectUpgrade().openDirectUpgrade({ feature?, tier?, source }).
 * Plan logic is in lib/upgradeTarget (unit tested); prices come from the
 * subscription_plans rows in /api/billing/upgrade-context.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Elements } from '@stripe/react-stripe-js';
import { ArrowLeft, Check, CheckCircle2, Clock, Shield, Sparkles } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { apiRequest } from '@/lib/queryClient';
import { previewRefusalMessage } from '@/lib/upgradePreviewRefusal';
import { analytics } from '@/lib/analytics';
import { formatUsd, type BillingInterval } from '@/lib/orderPlanCatalog';
import { requiredPlanFor, type GatedFeature, type PlanTier } from '@/lib/featureGate';
import {
  directUpgradeMode,
  intervalOfPlanId,
  keyBenefits,
  offerFor,
  primaryCtaLabel,
  priceShort,
  successHeadline,
  targetTierFor,
  tierOfPlanId,
  tierRank,
  type DirectUpgradeMode,
  type PlanRow,
} from '@/lib/upgradeTarget';
import { BillingUpgradeModal, PaymentMethodForm, isStripeConfigured, stripePromise } from './BillingUpgradeModal';

export interface DirectUpgradeRequest {
  /** The feature that was blocked; leads the benefits and picks the tier. */
  feature?: GatedFeature;
  /** The plan to offer, when the caller already knows it. */
  tier?: PlanTier | null;
  /** Where it was opened from, for analytics. */
  source?: string;
}

interface UpgradeCtx {
  currentSubscription?: { planId?: string | null; status?: string | null; stripeSubscriptionId?: string | null } | null;
  availablePlans?: PlanRow[];
  paymentMethods?: unknown[];
  hasDefaultPaymentMethod?: boolean;
  trialStatus?: 'active' | 'expired' | 'none';
  trialDaysRemaining?: number;
  organization?: { id?: string } | null;
}

type Step = 'offer' | 'card' | 'success';

/** Query keys that carry plan state; all refreshed after a change so gates unlock in place. */
const PLAN_STATE_KEYS = [
  ['/api/billing/upgrade-context'],
  ['/api/billing/dashboard'],
  ['/api/billing/subscription'],
  ['/api/billing/account-state'],
  ['/api/billing/payment-methods'],
  ['/api/billing'],
  ['/api/dashboard'],
  ['/api/organizations'],
  ['/api/capabilities'],
  ['/api/usage/status'],
];

/** The tier to offer for this request. */
export function chooseTier(req: DirectUpgradeRequest, mode: DirectUpgradeMode, currentTier: PlanTier | null): PlanTier | null {
  if (req.tier) return req.tier;
  if (mode === 'trial') {
    // Start the plan being trialled, unless the feature needs a higher one.
    const required = req.feature ? requiredPlanFor(req.feature) : null;
    if (required && tierRank(required) > tierRank(currentTier)) return required;
    return currentTier ?? 'lite';
  }
  return targetTierFor({ feature: req.feature, currentTier });
}

function DirectUpgradeBody({
  request,
  onClose,
  onCompareAll,
}: {
  request: DirectUpgradeRequest;
  onClose: () => void;
  onCompareAll: (planId?: string) => void;
}) {
  const queryClient = useQueryClient();
  const { data: ctx, isLoading } = useQuery<UpgradeCtx>({ queryKey: ['/api/billing/upgrade-context'], staleTime: 0 });
  const rows = ctx?.availablePlans ?? [];
  const mode = directUpgradeMode(ctx);
  const currentPlanId = ctx?.currentSubscription?.planId ?? null;
  const currentTier = tierOfPlanId(rows, currentPlanId);
  const tier = chooseTier(request, mode, currentTier);

  const [interval, setInterval] = useState<BillingInterval | null>(null);
  const effectiveInterval: BillingInterval = interval ?? intervalOfPlanId(rows, currentPlanId) ?? 'month';
  const offer = offerFor(rows, tier, effectiveInterval);

  const [step, setStep] = useState<Step>('offer');
  const [error, setError] = useState<string | null>(null);
  const [attemptToken, setAttemptToken] = useState(() => crypto.randomUUID());
  const threeDs = useRef(0);
  const [successName, setSuccessName] = useState<string | null>(null);
  const hasCard = !!ctx?.hasDefaultPaymentMethod || (ctx?.paymentMethods?.length ?? 0) > 0;
  const tracked = useRef(false);

  // No live subscription to change in place: only the full checkout grid can
  // start one, so go straight there on the chosen plan.
  useEffect(() => {
    if (isLoading || !ctx) return;
    if (mode === 'checkout') onCompareAll(offer?.planId);
    else if (!tracked.current) {
      tracked.current = true;
      analytics.track({
        event: 'upgrade_sheet_viewed', category: 'conversion', action: mode,
        label: offer?.name, plan: offer?.planId, feature: request.feature, source: request.source,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, mode]);

  const { data: preview, isLoading: previewLoading, error: previewError } = useQuery<{ amountDue: number; currency: string; prorationAmount: number; nextBillingDate: string | null }>({
    queryKey: ['/api/billing/upgrade-preview', offer?.planId],
    queryFn: () => apiRequest('GET', `/api/billing/upgrade-preview?planId=${encodeURIComponent(offer!.planId)}`),
    enabled: mode === 'change' && !!offer?.planId && offer.planId !== currentPlanId,
    staleTime: 30_000,
    retry: false,
  });
  // A 409 is the server refusing this change: say why and don't offer to go ahead.
  const refusal = mode === 'change' ? previewRefusalMessage(previewError) : null;

  const [setupSecret, setSetupSecret] = useState<string | null>(null);
  const setupIntent = useMutation({
    mutationFn: () => apiRequest('POST', '/api/billing/setup-intent'),
    onSuccess: (d: { client_secret: string }) => setSetupSecret(d.client_secret),
    onError: () => setError('Could not load the card form. Please try again.'),
  });
  const askForCard = () => {
    setStep('card');
    if (!setupSecret && !setupIntent.isPending) setupIntent.mutate();
  };

  const refreshPlanState = () => {
    for (const queryKey of PLAN_STATE_KEYS) queryClient.invalidateQueries({ queryKey });
  };

  const convert = useMutation({
    mutationFn: (vars: { planId: string; paymentMethodId?: string }) => {
      const endpoint = mode === 'trial' ? '/api/billing/trial/convert' : '/api/billing/upgrade';
      const prefix = mode === 'trial' ? 'trial' : 'upgrade';
      return apiRequest('POST', endpoint, { ...vars, attemptToken }, { idempotencyKey: `${prefix}:${vars.planId}:${attemptToken}` });
    },
    onSuccess: (data: any) => {
      refreshPlanState();
      // The org row follows the subscription webhook a moment later.
      setTimeout(refreshPlanState, 4000);
      if (data?.code === 'PAYMENT_PROCESSING') {
        setError(data.message || 'Your payment is processing. Your plan starts as soon as it completes.');
        return;
      }
      analytics.track({
        event: mode === 'trial' ? 'trial_converted_early' : 'plan_upgraded',
        category: mode === 'trial' ? 'conversion' : 'subscription',
        action: mode, label: offer?.name, value: offer?.price,
        plan: offer?.planId, fromPlan: currentPlanId, interval: offer?.interval,
        feature: request.feature, source: request.source,
      });
      setSuccessName(offer?.name ?? 'your new plan');
      setStep('success');
      setAttemptToken(crypto.randomUUID());
      threeDs.current = 0;
    },
    onError: async (err: any, vars) => {
      const body = err?.data ?? {};
      const code = body.code as string | undefined;
      if (code === 'REQUIRES_ACTION' && body.details?.clientSecret && threeDs.current < 2) {
        threeDs.current += 1;
        const stripe = await stripePromise;
        if (!stripe) { setError('Payments are unavailable right now. Please contact support.'); return; }
        const { error: confirmError } = await stripe.confirmCardPayment(body.details.clientSecret);
        if (confirmError) { setError(confirmError.message || 'The payment was not approved. Nothing has changed.'); return; }
        // Same attempt token: the server resumes the same Stripe attempt.
        convert.mutate(vars);
        return;
      }
      if (code === 'NEEDS_CARD') { askForCard(); return; }
      if (code === 'NOT_TRIALING' || body.requiresCheckout) { onCompareAll(); return; }
      if (code === 'CARD_DECLINED') {
        setError(body.message || 'Your card was declined. Try a different card.');
        setAttemptToken(crypto.randomUUID());
        return;
      }
      setError(body.message || err?.message || 'Something went wrong. Please try again.');
    },
  });

  const submit = (paymentMethodId?: string) => {
    if (!offer) return;
    setError(null);
    if (!paymentMethodId && !hasCard) { askForCard(); return; }
    convert.mutate({ planId: offer.planId, ...(paymentMethodId ? { paymentMethodId } : {}) });
  };

  if (isLoading || !ctx || mode === 'checkout') {
    return (
      <div className="space-y-3 py-2" data-testid="direct-upgrade-loading">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-11 w-full rounded-xl" />
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="flex flex-col items-center py-4 text-center" data-testid="direct-upgrade-success">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-brand-lime-soft">
          <CheckCircle2 className="h-7 w-7 text-brand-forest" aria-hidden="true" />
        </div>
        <SheetTitle className="text-xl" data-testid="text-direct-upgrade-success">
          {successHeadline(successName ?? 'your new plan', request.feature, mode)}
        </SheetTitle>
        <SheetDescription className="mt-1">
          {mode === 'trial' ? 'Your trial has ended and your plan is active. A receipt is on its way.' : 'Your new plan is active now.'}
        </SheetDescription>
        <Button className="mt-5 h-11 w-full rounded-xl bg-brand-lime font-semibold text-brand-ink hover:bg-brand-lime-hover" onClick={onClose} data-testid="button-direct-upgrade-done">
          Done
        </Button>
      </div>
    );
  }

  if (!offer) {
    // Already on the top plan (or the plan list didn't load).
    return (
      <div className="py-2" data-testid="direct-upgrade-top">
        <SheetHeader className="text-left">
          <SheetTitle>{currentTier === 'growth' ? "You're on our top plan" : 'Choose a plan'}</SheetTitle>
          <SheetDescription>Compare every plan to find the right fit.</SheetDescription>
        </SheetHeader>
        <Button className="mt-4 h-11 w-full rounded-xl bg-brand-lime font-semibold text-brand-ink hover:bg-brand-lime-hover" onClick={() => onCompareAll()}>
          Compare all plans
        </Button>
      </div>
    );
  }

  const benefits = keyBenefits({ planName: offer.name, feature: request.feature, mode });
  const days = ctx.trialDaysRemaining ?? null;
  const changingTrialPlan = mode === 'trial' && offer.planId !== currentPlanId;

  if (step === 'card') {
    return (
      <div className="space-y-4" data-testid="direct-upgrade-card">
        <button type="button" onClick={() => setStep('offer')} disabled={convert.isPending}
          className="flex min-h-[44px] items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
        </button>
        <SheetHeader className="text-left">
          <SheetTitle>Add a card to start {offer.name}</SheetTitle>
          <SheetDescription>{priceShort(offer.price, offer.interval)}. You'll be charged once the card is saved.</SheetDescription>
        </SheetHeader>
        {error && <p className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive" role="alert">{error}</p>}
        {!isStripeConfigured ? (
          <p className="text-sm text-destructive">Payments are unavailable right now. Please contact support.</p>
        ) : setupSecret ? (
          <Elements stripe={stripePromise} options={{ clientSecret: setupSecret, appearance: { theme: 'stripe' } }}>
            <PaymentMethodForm
              onPaymentReady={(pm) => submit(pm)}
              onError={(m) => setError(m)}
              processing={convert.isPending}
            />
          </Elements>
        ) : (
          <Skeleton className="h-32 w-full rounded-xl" />
        )}
      </div>
    );
  }

  return (
    <div data-testid="direct-upgrade-offer">
      <SheetHeader className="text-left">
        <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-lime-soft">
          <Sparkles className="h-5 w-5 text-brand-forest" aria-hidden="true" />
        </div>
        <SheetTitle data-testid="text-direct-upgrade-title">
          {mode === 'trial' ? `Start your ${offer.name} plan now` : `Upgrade to ${offer.name}`}
        </SheetTitle>
        <SheetDescription>
          {mode === 'trial'
            ? 'Your trial ends now and billing starts today.'
            : request.feature ? `${benefits[0]} is included in ${offer.name}.` : `The next step up from your current plan.`}
        </SheetDescription>
      </SheetHeader>

      {mode === 'trial' && days != null && days > 0 && (
        <p className="mt-3 flex items-center gap-2 rounded-xl bg-muted px-3 py-2 text-[13px]" data-testid="text-direct-upgrade-trial-days">
          <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
          {days} {days === 1 ? 'day' : 'days'} left in your trial. Starting now skips the rest of it.
        </p>
      )}

      {offer.bothIntervals && (
        <div className="mt-4 grid grid-cols-2 gap-1 rounded-xl bg-muted p-1" role="radiogroup" aria-label="Billing period">
          {(['month', 'year'] as const).map((i) => (
            <button
              key={i}
              type="button"
              role="radio"
              aria-checked={offer.interval === i}
              onClick={() => setInterval(i)}
              className={`min-h-[40px] rounded-lg text-sm font-medium transition-colors ${offer.interval === i ? 'bg-brand-lime text-brand-ink shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`button-direct-upgrade-interval-${i}`}
            >
              {i === 'month' ? 'Monthly' : 'Annual'}
              {i === 'year' && offer.annualSaving > 0 && <span className="ml-1 text-[11px] font-semibold">save {formatUsd(offer.annualSaving)}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="mt-4 rounded-2xl border border-border p-4">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-base font-semibold">{offer.name}</p>
          <p className="text-lg font-semibold" data-testid="text-direct-upgrade-price">
            {offer.interval === 'year' ? `${formatUsd(offer.price)}/yr` : `${formatUsd(offer.price)}/mo`}
          </p>
        </div>
        {offer.interval === 'year' && (
          <p className="text-[12px] text-muted-foreground">Works out at {formatUsd(offer.price / 12)} a month{offer.annualSaving > 0 ? ` · save ${formatUsd(offer.annualSaving)} a year` : ''}</p>
        )}
        <ul className="mt-3 space-y-2" data-testid="list-direct-upgrade-benefits">
          {benefits.map((b, i) => (
            <li key={b} className={`flex items-start gap-2 text-[13.5px] ${i === 0 ? 'font-semibold' : ''}`}>
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-forest" aria-hidden="true" />
              {b}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-3 text-[12.5px] text-muted-foreground" data-testid="text-direct-upgrade-billing">
        {mode === 'trial' ? (
          <>You'll be charged {formatUsd(offer.price)} today{changingTrialPlan ? ` for ${offer.name} instead of your trial plan` : ''}, then every {offer.interval === 'year' ? 'year' : 'month'}. Outbound calling, SMS and the full plan allowances unlock straight away.</>
        ) : previewLoading ? (
          <Skeleton className="h-4 w-2/3" />
        ) : refusal ? (
          <span className="font-medium text-destructive" role="alert" data-testid="text-direct-upgrade-refusal">{refusal}</span>
        ) : preview ? (
          <>Charged today: <span className="font-semibold text-foreground">{formatUsd(preview.amountDue)}</span> (prorated for the rest of this period){preview.nextBillingDate ? `, then ${priceShort(offer.price, offer.interval)} from ${new Date(preview.nextBillingDate).toLocaleDateString()}` : ''}.</>
        ) : (
          <>The difference is prorated on your next invoice.</>
        )}
      </div>

      {error && <p className="mt-3 rounded-xl bg-destructive/10 p-3 text-sm text-destructive" role="alert" data-testid="text-direct-upgrade-error">{error}</p>}

      <Button
        className="mt-4 h-12 w-full rounded-xl bg-brand-lime text-[15px] font-semibold text-brand-ink hover:bg-brand-lime-hover disabled:bg-brand-lime disabled:opacity-70"
        onClick={() => submit()}
        disabled={convert.isPending || offer.planId === currentPlanId && mode !== 'trial' || !!refusal || (mode === 'change' && previewLoading)}
        data-testid="button-direct-upgrade-primary"
      >
        {convert.isPending ? 'Processing…' : primaryCtaLabel(mode, offer.name, offer.price, offer.interval)}
      </Button>
      <p className="mt-2 flex items-center justify-center gap-1.5 text-[11.5px] text-muted-foreground">
        <Shield className="h-3.5 w-3.5" aria-hidden="true" /> {hasCard ? 'Billed to your card on file. Cancel anytime.' : 'Secured by Stripe. Cancel anytime.'}
      </p>

      <div className="mt-2 flex flex-col">
        <button
          type="button"
          onClick={() => onCompareAll()}
          className="min-h-[44px] text-[13.5px] font-medium text-brand-ink underline decoration-brand-lime decoration-2 underline-offset-4 dark:text-foreground"
          data-testid="button-direct-upgrade-compare"
        >
          {mode === 'trial' ? 'Choose a different plan' : 'Compare all plans'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="min-h-[44px] text-[13px] text-muted-foreground hover:text-foreground"
          data-testid="button-direct-upgrade-dismiss"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

export function DirectUpgradeSheet({
  open,
  onOpenChange,
  request,
  onCompareAll,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: DirectUpgradeRequest;
  onCompareAll: (planId?: string) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="mx-auto h-auto max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl pb-[max(1rem,env(safe-area-inset-bottom))] sm:bottom-4 sm:rounded-2xl"
        data-testid="sheet-direct-upgrade"
      >
        {/* Remount per open so a finished attempt never leaks into the next. */}
        {open && <DirectUpgradeBody request={request} onClose={() => onOpenChange(false)} onCompareAll={onCompareAll} />}
      </SheetContent>
    </Sheet>
  );
}

interface DirectUpgradeContextValue {
  openDirectUpgrade: (req?: DirectUpgradeRequest) => void;
  /** The full plan grid, optionally on a plan. */
  openPlanGrid: (planId?: string) => void;
}

const DirectUpgradeContext = createContext<DirectUpgradeContextValue | null>(null);

/** Mount once, inside the query client. Renders the sheet and the full grid it falls back to. */
export function DirectUpgradeProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<DirectUpgradeRequest>({});
  const [open, setOpen] = useState(false);
  const [grid, setGrid] = useState<{ open: boolean; planId?: string }>({ open: false });

  const openDirectUpgrade = useCallback((req: DirectUpgradeRequest = {}) => {
    setRequest(req);
    setOpen(true);
  }, []);
  const openPlanGrid = useCallback((planId?: string) => {
    setOpen(false);
    setGrid({ open: true, planId });
  }, []);
  const value = useMemo(() => ({ openDirectUpgrade, openPlanGrid }), [openDirectUpgrade, openPlanGrid]);

  return (
    <DirectUpgradeContext.Provider value={value}>
      {children}
      <DirectUpgradeSheet open={open} onOpenChange={setOpen} request={request} onCompareAll={openPlanGrid} />
      {grid.open && (
        <BillingUpgradeModal accent="brand" forceShow preselectedPlanId={grid.planId} onClose={() => setGrid({ open: false })} />
      )}
    </DirectUpgradeContext.Provider>
  );
}

/** Open the focused upgrade. Outside a provider, `available` is false. */
export function useDirectUpgrade() {
  const ctx = useContext(DirectUpgradeContext);
  return {
    available: !!ctx,
    openDirectUpgrade: ctx?.openDirectUpgrade ?? (() => {}),
    openPlanGrid: ctx?.openPlanGrid ?? (() => {}),
  };
}
